import { useQuery } from "@tanstack/react-query";
import { projectionService, workQueueService } from "@/core/api/services";
import { workbenchKeys } from "@/core/api/queryKeys";
import { parseProjectionData } from "./projectionUtils";

export interface DailyTeamStats {
  date: string;
  itemsCreated: number;
  itemsCompleted: number;
  slaBreaches: number;
  escalations: number;
  avgCycleTimeMinutes: number;
  reworkCount: number;
  totalItems: number;
}

export interface TeamStatsResult {
  daily: DailyTeamStats[];
  queueStats: {
    total: number;
    overdue: number;
    unassigned: number;
    byStatus: Record<string, number>;
    byType: Record<string, number>;
  };
}

export function useTeamStats(teamId: string, dateRange: string) {
  const limit = dateRange === "30d" ? 30 : 7;

  return useQuery({
    queryKey: workbenchKeys.dashboard.team(teamId, dateRange),
    queryFn: async (): Promise<TeamStatsResult> => {
      const [projResponse, queueResponse] = await Promise.all([
        projectionService.query({
          type: "workqueue_daily_stats",
          filters: { team: teamId },
          orderBy: "date",
          descending: true,
          limit,
          offset: 0,
        }),
        workQueueService.getQueueStats({}),
      ]);

      const daily: DailyTeamStats[] = (projResponse.projections ?? []).map((p) => {
        const d = parseProjectionData(p.data);
        return {
          date: (d.date as string) ?? "",
          itemsCreated: (d.items_created as number) ?? 0,
          itemsCompleted: (d.items_completed as number) ?? 0,
          slaBreaches: (d.sla_breaches as number) ?? 0,
          escalations: (d.escalations as number) ?? 0,
          avgCycleTimeMinutes: (d.avg_cycle_time_minutes as number) ?? 0,
          reworkCount: (d.rework_count as number) ?? 0,
          totalItems: (d.total_items as number) ?? 0,
        };
      }).reverse();

      const stats = queueResponse.stats;
      return {
        daily,
        queueStats: {
          total: stats?.total ?? 0,
          overdue: stats?.overdue ?? 0,
          unassigned: stats?.unassigned ?? 0,
          byStatus: Object.fromEntries(Object.entries(stats?.byStatus ?? {})),
          byType: Object.fromEntries(Object.entries(stats?.byType ?? {})),
        },
      };
    },
    enabled: !!teamId,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}
